/**
 * Canonical competition timing rules — the single source of truth for the
 * club's competition schedule, imported by both the frontend
 * (`@shared/competitionRules`) and the backend (`amplify/functions/**`),
 * following the same pattern as `shared/competitions.ts` and `shared/fees.ts`.
 *
 * See `.kiro/specs/competition-timing-canonical/requirements.md` for the
 * rules themselves and the defects that made this file necessary — most
 * notably that online sign-on was closing at fishing start instead of two
 * hours later, locking members out during the August competition.
 *
 * Everything here is expressed as an OFFSET from fishing start (`eventDate`),
 * in minutes, rather than an absolute wall-clock time. An absolute "09:00"
 * would break every non-standard event and every test fixture (e2e creates
 * events a few hours from now, not at 7am on a Sunday). Offsets give exactly
 * the club rule for the standard 07:00 competition and degrade sensibly for
 * anything else.
 */

/**
 * IANA timezone the club operates in. All admin-facing competition times are
 * entered and displayed in this zone, DST-aware (AEDT begins 4 Oct 2026).
 */
export const CLUB_TIMEZONE = 'Australia/Sydney';

/**
 * Canonical offsets from fishing start (`eventDate`), in minutes. ~99.9% of
 * competitions use these unchanged; deviation must be possible but deliberate
 * (see `competitionStartTime`/`competitionEndTime` override support).
 */
export const COMPETITION_DEFAULTS = {
  /** 07:00 -> 14:00 */
  fishingDurationMinutes: 7 * 60,
  /** Online sign-on opens 24h before fishing start (Sat 07:00). */
  signOnOpensBeforeMinutes: 24 * 60,
  /** Online sign-on closes 2h after fishing start (07:00 -> 09:00). */
  signOnClosesAfterMinutes: 2 * 60,
  /**
   * Grace period after the catch window closes. #134 (D6): the competition
   * ends at 2:00 pm sharp, so new and duplicated competitions default to 0.
   */
  gracePeriodMinutes: 0,
} as const;

/**
 * Physical sign-on desk hours. Informational only — this is what gets printed
 * in event copy and shown to members, but it is NEVER used to gate online
 * sign-on, catch submission, or any other enforcement. The desk being staffed
 * 07:00-09:00 is a separate fact from the online sign-on window being open for
 * the same two hours; they happen to share the same clock times under the
 * canonical rules, but only the latter is enforced in code.
 */
export const SIGN_ON_DESK_HOURS = { open: '07:00', close: '09:00' } as const;

export interface DerivedCompetitionTimes {
  /** = eventDate. Catch window opens. */
  competitionStartTime: string;
  /** competitionStartTime + fishingDurationMinutes. Catch window closes — canonical end. */
  competitionEndTime: string;
  /** competitionStartTime - signOnOpensBeforeMinutes. Online sign-on opens. */
  registrationOpenTime: string;
  /** competitionStartTime + signOnClosesAfterMinutes. Online sign-on closes. */
  registrationCloseTime: string;
  /** Grace period after competitionEndTime, in minutes. */
  gracePeriodMinutes: number;
}

/**
 * Derives all five canonical competition times from just the fishing start
 * (`eventDate`), using the club's default offsets. This is what a new
 * competition should be populated with, and what a duplicated competition
 * should be re-derived from after its date is shifted — never a fixed
 * millisecond delta, which drifts an hour out across a DST boundary.
 *
 * @param eventDate ISO 8601 instant — fishing start.
 * @throws if eventDate is not a valid date string.
 */
export function deriveCompetitionTimes(eventDate: string): DerivedCompetitionTimes {
  const start = new Date(eventDate);
  if (Number.isNaN(start.getTime())) {
    throw new Error(`deriveCompetitionTimes: invalid eventDate "${eventDate}"`);
  }

  const addMinutes = (date: Date, minutes: number): string =>
    new Date(date.getTime() + minutes * 60 * 1000).toISOString();

  return {
    competitionStartTime: start.toISOString(),
    competitionEndTime: addMinutes(start, COMPETITION_DEFAULTS.fishingDurationMinutes),
    registrationOpenTime: addMinutes(start, -COMPETITION_DEFAULTS.signOnOpensBeforeMinutes),
    registrationCloseTime: addMinutes(start, COMPETITION_DEFAULTS.signOnClosesAfterMinutes),
    gracePeriodMinutes: COMPETITION_DEFAULTS.gracePeriodMinutes,
  };
}

// ---------------------------------------------------------------------------
// #113: clock-driven competition gates
// ---------------------------------------------------------------------------

/**
 * Member and guest check-in stays open this many minutes past
 * registrationCloseTime (09:10 for a 09:00 close). Displayed close times still
 * show the real registrationCloseTime. Applied by deriveLifecycle
 * (signOnAcceptsUntil).
 */
export const CHECKIN_CLOSE_TOLERANCE_MINUTES = 10;

// ---------------------------------------------------------------------------
// #134: one lifecycle, derived from the competition's own times. Only human
// decisions are stored (adjudicatedAt, publishedAt, cancelledAt). No stored
// Open/Closed state, no override flags, no timers.
// ---------------------------------------------------------------------------

export interface CompetitionFacts {
  eventDate?: string | null;
  registrationOpenTime?: string | null;
  registrationCloseTime?: string | null;
  competitionStartTime?: string | null;
  competitionEndTime?: string | null;
  gracePeriodMinutes?: number | null;
  adjudicatedAt?: string | null;
  publishedAt?: string | null;
  cancelledAt?: string | null;
}

export type Phase =
  | 'CANCELLED' | 'PUBLISHED' | 'ADJUDICATED' | 'TIMING_INCOMPLETE'
  | 'ENDED' | 'FISHING' | 'SIGN_ON' | 'SCHEDULED';

export type SignOnReason = 'OPEN' | 'NOT_YET_OPEN' | 'CLOSED' | 'NO_TIMES' | 'CANCELLED' | 'ADJUDICATED';
export type CatchReason = 'OPEN' | 'NOT_STARTED' | 'ENDED' | 'TIMING_INCOMPLETE' | 'CANCELLED' | 'ADJUDICATED';

export interface Lifecycle {
  phase: Phase;
  signOnOpen: boolean;
  catchesOpen: boolean;
  /** Fishing has started (start <= now), whatever else is true. */
  started: boolean;
  ended: boolean;
  adjudicated: boolean;
  published: boolean;
  cancelled: boolean;
  timingComplete: boolean;
  signOnOpensAt: Date | null;
  /** The displayed close (registrationCloseTime). */
  signOnClosesAt: Date | null;
  /** The real close: signOnClosesAt + CHECKIN_CLOSE_TOLERANCE_MINUTES. Exclusive. */
  signOnAcceptsUntil: Date | null;
  startsAt: Date | null;
  endsAt: Date | null;
  graceMinutes: number;
  /** endsAt + grace. Exclusive: a catch at exactly this instant is refused. */
  catchDeadline: Date | null;
  signOnReason: SignOnReason;
  catchReason: CatchReason;
}

const MINUTE = 60 * 1000;

function toDate(v: string | null | undefined): Date | null {
  if (!v) return null;
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Missing, null, negative or non-numeric grace means 0; an explicit 0 is 0. */
export function resolveGraceMinutes(g: number | null | undefined): number {
  return typeof g === 'number' && Number.isFinite(g) && g > 0 ? g : 0;
}

/** A decision is recorded by its dated field only. */
export function isAdjudicated(f: Pick<CompetitionFacts, 'adjudicatedAt'> | null | undefined): boolean {
  return !!f?.adjudicatedAt;
}
export function isPublished(f: Pick<CompetitionFacts, 'publishedAt'> | null | undefined): boolean {
  return !!f?.publishedAt;
}
export function isCancelled(f: Pick<CompetitionFacts, 'cancelledAt'> | null | undefined): boolean {
  return !!f?.cancelledAt;
}

/**
 * The one lifecycle function (#134 R1). Sign-on: open from
 * registrationOpenTime (default eventDate - 24 h) until registrationCloseTime
 * (default eventDate + 2 h) plus the 10-minute tolerance; open bound
 * inclusive, close exclusive. Catches: competitionStartTime <= now <
 * competitionEndTime + grace, not adjudicated, not cancelled; missing start
 * or end fails closed (TIMING_INCOMPLETE). The phase is a label only: sign-on
 * and fishing overlap on the day.
 */
export function deriveLifecycle(f: CompetitionFacts, now: Date): Lifecycle {
  const eventDate = toDate(f.eventDate ?? null);
  const signOnOpensAt = toDate(f.registrationOpenTime ?? null)
    ?? (eventDate ? new Date(eventDate.getTime() - COMPETITION_DEFAULTS.signOnOpensBeforeMinutes * MINUTE) : null);
  const signOnClosesAt = toDate(f.registrationCloseTime ?? null)
    ?? (eventDate ? new Date(eventDate.getTime() + COMPETITION_DEFAULTS.signOnClosesAfterMinutes * MINUTE) : null);
  const signOnAcceptsUntil = signOnClosesAt ? new Date(signOnClosesAt.getTime() + CHECKIN_CLOSE_TOLERANCE_MINUTES * MINUTE) : null;
  const startsAt = toDate(f.competitionStartTime ?? null);
  const endsAt = toDate(f.competitionEndTime ?? null);
  const graceMinutes = resolveGraceMinutes(f.gracePeriodMinutes);
  const timingComplete = !!startsAt && !!endsAt;
  const catchDeadline = endsAt ? new Date(endsAt.getTime() + graceMinutes * MINUTE) : null;

  const adjudicated = isAdjudicated(f);
  const published = isPublished(f);
  const cancelled = isCancelled(f);
  const t = now.getTime();
  const started = !!startsAt && t >= startsAt.getTime();
  const ended = !!catchDeadline && t >= catchDeadline.getTime();

  let signOnReason: SignOnReason;
  if (cancelled) signOnReason = 'CANCELLED';
  else if (adjudicated) signOnReason = 'ADJUDICATED';
  else if (!signOnOpensAt || !signOnAcceptsUntil) signOnReason = 'NO_TIMES';
  else if (t < signOnOpensAt.getTime()) signOnReason = 'NOT_YET_OPEN';
  else if (t >= signOnAcceptsUntil.getTime()) signOnReason = 'CLOSED';
  else signOnReason = 'OPEN';

  let catchReason: CatchReason;
  if (cancelled) catchReason = 'CANCELLED';
  else if (adjudicated) catchReason = 'ADJUDICATED';
  else if (!timingComplete) catchReason = 'TIMING_INCOMPLETE';
  else if (!started) catchReason = 'NOT_STARTED';
  else if (ended) catchReason = 'ENDED';
  else catchReason = 'OPEN';

  let phase: Phase;
  if (cancelled) phase = 'CANCELLED';
  else if (published) phase = 'PUBLISHED';
  else if (adjudicated) phase = 'ADJUDICATED';
  else if (!timingComplete) phase = 'TIMING_INCOMPLETE';
  else if (ended) phase = 'ENDED';
  else if (started) phase = 'FISHING';
  else if (signOnReason === 'OPEN') phase = 'SIGN_ON';
  else phase = 'SCHEDULED';

  return {
    phase,
    signOnOpen: signOnReason === 'OPEN',
    catchesOpen: catchReason === 'OPEN',
    started,
    ended,
    adjudicated,
    published,
    cancelled,
    timingComplete,
    signOnOpensAt,
    signOnClosesAt,
    signOnAcceptsUntil,
    startsAt,
    endsAt,
    graceMinutes,
    catchDeadline,
    signOnReason,
    catchReason,
  };
}

/**
 * Admin late check-in (#113, kept): an admin may check anyone in from sign-on
 * opening until the catch deadline (exclusive), unless cancelled or
 * adjudicated.
 */
export function isAdminLateCheckInAllowed(f: CompetitionFacts, now: Date): boolean {
  const l = deriveLifecycle(f, now);
  if (l.cancelled || l.adjudicated || !l.signOnOpensAt || !l.catchDeadline) return false;
  const t = now.getTime();
  return t >= l.signOnOpensAt.getTime() && t < l.catchDeadline.getTime();
}

/** Happy snaps: from fishing start until the competition is adjudicated; never when cancelled. */
export function happySnapsOpen(f: CompetitionFacts, now: Date): boolean {
  const l = deriveLifecycle(f, now);
  return l.started && !l.adjudicated && !l.cancelled;
}

/**
 * The mystery fish rule (#134 D3, one implementation): same species, and the
 * length within 0.05 cm, or species only when no size is set.
 */
export function isMysteryFishMatch(
  catchSpecies: string | null | undefined,
  catchLengthCm: number | null | undefined,
  mysterySpecies: string | null | undefined,
  mysterySizeCm: number | null | undefined,
): boolean {
  if (!mysterySpecies || catchSpecies !== mysterySpecies) return false;
  if (mysterySizeCm === null || mysterySizeCm === undefined || Number(mysterySizeCm) === 0) return true;
  return Math.abs(Number(catchLengthCm) - Number(mysterySizeCm)) < 0.05;
}

/** Member-facing wording for a refused catch (unchanged from before #134). */
export function catchRefusalMessage(reason: CatchReason): string {
  switch (reason) {
    case 'NOT_STARTED': return 'Competition has not started yet';
    case 'ENDED': return 'Submission window has closed';
    case 'TIMING_INCOMPLETE': return 'Competition is not currently open for submissions';
    case 'CANCELLED': return 'This competition has been cancelled';
    case 'ADJUDICATED': return 'Submission window has closed';
    case 'OPEN': return '';
  }
}

/**
 * Plain-English status lines for the admin panel (#134 R6.4), from the same
 * facts as the gates so the words can never disagree with them. `fmt` formats
 * an instant in club time (frontend: formatClubTime; Lambdas need no
 * time-zone code).
 */
export function describeLifecycle(f: CompetitionFacts, now: Date, fmt: (d: Date) => string): { signOn: string; catches: string } {
  const l = deriveLifecycle(f, now);
  let signOn: string;
  switch (l.signOnReason) {
    case 'CANCELLED': signOn = 'Sign-on closed: the competition was cancelled.'; break;
    case 'ADJUDICATED': signOn = 'Sign-on closed: the competition has been adjudicated.'; break;
    case 'NO_TIMES': signOn = 'Sign-on closed: the sign-on times are not set.'; break;
    case 'NOT_YET_OPEN': signOn = `Sign-on opens at ${fmt(l.signOnOpensAt as Date)}.`; break;
    case 'CLOSED': signOn = `Sign-on closed at ${fmt(l.signOnClosesAt as Date)}.`; break;
    default:
      signOn = `Sign-on open until ${fmt(l.signOnAcceptsUntil as Date)} (closes ${fmt(l.signOnClosesAt as Date)}, ${CHECKIN_CLOSE_TOLERANCE_MINUTES} minutes tolerance).`;
  }
  let catches: string;
  const graceText = l.graceMinutes > 0 ? ` (grace ${l.graceMinutes} minutes)` : '';
  switch (l.catchReason) {
    case 'CANCELLED': catches = 'Catches closed: the competition was cancelled.'; break;
    case 'ADJUDICATED': catches = 'Catches closed: the competition has been adjudicated.'; break;
    case 'TIMING_INCOMPLETE': catches = 'Catches closed: the fishing start or end time is not set.'; break;
    case 'NOT_STARTED': catches = `Catches open at ${fmt(l.startsAt as Date)}, accepted until ${fmt(l.catchDeadline as Date)}${graceText}.`; break;
    case 'ENDED': catches = `Catches closed: the competition ended at ${fmt(l.catchDeadline as Date)}.`; break;
    default: catches = `Catches accepted until ${fmt(l.catchDeadline as Date)}${graceText}.`;
  }
  return { signOn, catches };
}

// --- Club-time calendar arithmetic (no dependencies), for rescheduling ------

function clubOffsetMinutes(at: Date): number {
  const parts = new Intl.DateTimeFormat('en-AU', {
    timeZone: CLUB_TIMEZONE, hourCycle: 'h23',
    year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
  }).formatToParts(at);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value);
  const asUtc = Date.UTC(get('year'), get('month') - 1, get('day'), get('hour'), get('minute'), get('second'));
  return Math.round((asUtc - at.getTime()) / MINUTE);
}

interface ClubWall { y: number; m: number; d: number; hh: number; mi: number; ss: number; ms: number }

function toClubWall(at: Date): ClubWall {
  const local = new Date(at.getTime() + clubOffsetMinutes(at) * MINUTE);
  return { y: local.getUTCFullYear(), m: local.getUTCMonth(), d: local.getUTCDate(), hh: local.getUTCHours(), mi: local.getUTCMinutes(), ss: local.getUTCSeconds(), ms: local.getUTCMilliseconds() };
}

function fromClubWall(w: ClubWall): Date {
  const guess = Date.UTC(w.y, w.m, w.d, w.hh, w.mi, w.ss, w.ms);
  let t = guess - clubOffsetMinutes(new Date(guess)) * MINUTE;
  t = guess - clubOffsetMinutes(new Date(t)) * MINUTE; // second pass settles a DST edge
  return new Date(t);
}

function clubDayNumber(w: ClubWall): number {
  return Math.round(Date.UTC(w.y, w.m, w.d) / (24 * 60 * MINUTE));
}

/**
 * Reschedule (#134 R7.1): move every competition time to a new eventDate,
 * keeping each one's calendar offset from eventDate in club time (for example
 * "the day before at 7:00 am" stays the day before at 7:00 am across a DST
 * change), rather than a fixed millisecond delta.
 */
export function rescheduleTimes(
  old: Pick<CompetitionFacts, 'eventDate' | 'registrationOpenTime' | 'registrationCloseTime' | 'competitionStartTime' | 'competitionEndTime'>,
  newEventDate: string,
): { eventDate: string; registrationOpenTime?: string; registrationCloseTime?: string; competitionStartTime?: string; competitionEndTime?: string } {
  const oldDate = toDate(old.eventDate ?? null);
  const next = toDate(newEventDate);
  if (!oldDate || !next) throw new Error('rescheduleTimes: eventDate and newEventDate must be valid dates');
  const oldWall = toClubWall(oldDate);
  const nextWall = toClubWall(next);
  const shift = (iso: string | null | undefined): string | undefined => {
    const d = toDate(iso ?? null);
    if (!d) return undefined;
    const w = toClubWall(d);
    const dayOffset = clubDayNumber(w) - clubDayNumber(oldWall);
    const target = new Date(Date.UTC(nextWall.y, nextWall.m, nextWall.d + dayOffset));
    return fromClubWall({ y: target.getUTCFullYear(), m: target.getUTCMonth(), d: target.getUTCDate(), hh: w.hh, mi: w.mi, ss: w.ss, ms: w.ms }).toISOString();
  };
  const out: ReturnType<typeof rescheduleTimes> = { eventDate: next.toISOString() };
  for (const k of ['registrationOpenTime', 'registrationCloseTime', 'competitionStartTime', 'competitionEndTime'] as const) {
    const v = shift(old[k]);
    if (v) out[k] = v;
  }
  return out;
}
